import { DefaultTheme, LinkingOptions, NavigationContainer } from "@react-navigation/native";
import { registerRootComponent } from "expo";
import React, { useEffect, useState, useMemo } from "react";
import { Animated, StyleSheet, View } from "react-native";
import { HomeStack } from "../navigation/HomeStack";
import { appConfigStore } from "./stores/AppConfig";
import { prefix, RootPaths, themeColors, darkBackgroundSecondary, lightBackgroundSecondary } from '../config/constants';
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeTransitionOverlay } from "../components/ThemeTransitionOverlay";
import { ConfigLoadingOverlay } from "../components/ConfigLoadingOverlay";
import { DynamicMetaTags } from "../components/DynamicMetaTags";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { ToastContainer, Bounce } from "react-toastify";
import { HelmetProvider } from 'react-helmet-async';
import "react-toastify/dist/ReactToastify.css";
import { StoreConfigProvider, useStoreConfig } from '../contexts/StoreConfigContext';
import { MaintenanceScreen } from '../components/MaintenanceScreen';
import { ClaimStoreScreen } from '../components/ClaimStoreScreen';
import { ComingSoonScreen } from '../components/ComingSoonScreen';
import { useAppInitialization } from '../services/InitializationService';
import { userStore } from './stores/User';
import { AccessLevel } from '../models/enums/AccessLevel';
import AuthService from '../services/supabase/AuthService';
import '../global.css';

// Declare a module augmentation for the custom theme
declare module "@mui/material/styles" {
	interface Theme {
		customColors: {
			background: string;
			backgroundSecondary: string;
			backgroundTertiary: string;
			textPrimary: string;
			textSecondary: string;
			textMuted: string;
			border: string;
			divider: string;
			hover: string;
			buttonOutline: string;
			cardBackground: string;
			navBackground: string;
			inputBackground: string;
		};
	}

	interface ThemeOptions {
		customColors: {
			background: string;
			backgroundSecondary: string;
			backgroundTertiary: string;
			textPrimary: string;
			textSecondary: string;
			textMuted: string;
			border: string;
			divider: string;
			hover: string;
			buttonOutline: string;
			cardBackground: string;
			navBackground: string;
			inputBackground: string;
		};
	}
}

// Separate memoized navigation container to prevent unnecessary remounts
const NavigationContainerWrapper = React.memo(({ theme, darkMode }: { theme: any; darkMode: boolean }) => {
	const [fadeAnimation] = useState(new Animated.Value(1)); // Start at 1 since we hide with overlay

	// Memoize linking config to prevent NavigationContainer remounts
	const linking = useMemo<LinkingOptions<ReactNavigation.RootParamList>>(() => ({
		config: {
			screens: RootPaths
		},
		prefixes: [prefix],
	}), []);

	// Memoize navigator theme
	const navigatorTheme = useMemo(() => ({
		...DefaultTheme,
		colors: {
			...DefaultTheme.colors,
			background: darkMode ? darkBackgroundSecondary : lightBackgroundSecondary,
		},
	}), [darkMode]);

	return (
		<ThemeProvider theme={theme}>
			<CssBaseline />
			<NavigationContainer
				linking={linking}
				theme={navigatorTheme}
			>
				<Animated.View
					style={{
						...styles.container,
						...{ opacity: fadeAnimation },
					}}
				>
					<HomeStack />
				</Animated.View>
			</NavigationContainer>
		</ThemeProvider>
	);
});

NavigationContainerWrapper.displayName = 'NavigationContainerWrapper';

const AppContent = () => {
	const darkMode = appConfigStore((state) => state.darkMode);
	const toggleDarkMode = appConfigStore((state) => state.toggleDarkMode);
	const showOverlay = appConfigStore((state) => state.showOverlay);
	const setShowOverlay = appConfigStore((state) => state.setShowOverlay);
	const setAppReady = appConfigStore((state) => state.setAppReady);
	const { isInactive, inactivePrimaryColor, companyName, isNotFound, notFoundSubdomain, kycStatus, kycResolved, reloadConfig } = useStoreConfig();
	const userRole = userStore((state) => state.user.role);
	const isAuthenticated = userStore((state) => state.user.authenticated);
	const userInfo = userStore((state) => state.user.info);
	const isAdminUser = isAuthenticated && (
		userRole === AccessLevel.Admin ||
		userRole === AccessLevel.Moderator ||
		userInfo?.user_metadata?.accountType === 'store_owner'
	);
	const { signInWithEmailAndPassword } = AuthService();

	const handleComingSoonLogin = async (email: string, password: string) => {
		const result = await signInWithEmailAndPassword(email, password);
		if (result.success) {
			await reloadConfig();
		}
		return result;
	};

	// Use centralized initialization service
	const { isReady, config } = useAppInitialization();

	const theme = useMemo(
		() => {
			const currentTheme = darkMode ? themeColors.dark : themeColors.light;

			return createTheme({
				palette: {
					mode: darkMode ? "dark" : "light",
					primary: {
						main: config.primaryColor,
					},
					secondary: {
						main: "#f50057",
					},
					background: {
						default: currentTheme.background,
						paper: currentTheme.cardBackground,
					},
					text: {
						primary: currentTheme.textPrimary,
						secondary: currentTheme.textSecondary,
					},
					divider: currentTheme.divider,
				},
				typography: {
					fontFamily: "primaryFont, Arial, sans-serif",
					// Apply the font family to all typography variants
					allVariants: {
						fontFamily: "primaryFont, Arial, sans-serif",
					},
					h1: {
						fontWeight: 700,
						letterSpacing: 1,
						fontFamily: "primaryFont, Arial, sans-serif",
					},
					h2: {
						fontWeight: 700,
						letterSpacing: 1,
						fontFamily: "primaryFont, Arial, sans-serif",
					},
					h3: {
						fontWeight: 700,
						letterSpacing: 1,
						fontFamily: "primaryFont, Arial, sans-serif",
					},
					h4: {
						fontWeight: 600,
						letterSpacing: 0.75,
						fontFamily: "primaryFont, Arial, sans-serif",
					},
					h5: {
						fontWeight: 600,
						letterSpacing: 0.5,
						fontFamily: "primaryFont, Arial, sans-serif",
					},
					h6: {
						fontWeight: 600,
						letterSpacing: 0.5,
						fontFamily: "primaryFont, Arial, sans-serif",
					},
					subtitle1: {
						fontWeight: 500,
						letterSpacing: 0.5,
						fontFamily: "primaryFont, Arial, sans-serif",
					},
					subtitle2: {
						fontWeight: 500,
						letterSpacing: 0.25,
						fontFamily: "primaryFont, Arial, sans-serif",
					},
					body1: {
						letterSpacing: 0.5,
						fontFamily: "primaryFont, Arial, sans-serif",
					},
					body2: {
						letterSpacing: 0.25,
						fontFamily: "primaryFont, Arial, sans-serif",
					},
					button: {
						letterSpacing: 0.75,
						textTransform: "none",
						fontFamily: "primaryFont, Arial, sans-serif",
					},
					caption: {
						letterSpacing: 0.4,
						fontFamily: "primaryFont, Arial, sans-serif",
					},
				},
				components: {
					MuiButton: {
						styleOverrides: {
							root: {
								borderRadius: 8,
								textTransform: "none",
							},
						},
					},
					MuiTypography: {
						styleOverrides: {
							root: {
								// Any global typography overrides
							},
						},
					},
				},
				// Add our custom colors to the theme for easy access
				customColors: currentTheme,
			});
		},
		[darkMode, config]
	);

	// Sync initialization state to appConfigStore
	useEffect(() => {
		if (isReady) {
			setAppReady(true);
		}
	}, [isReady, setAppReady]);

	const handleOverlayComplete = () => {
		toggleDarkMode();
		setShowOverlay(false);
	};

	// Show claim store screen if store not found
	if (isNotFound) {
		return (
			<ClaimStoreScreen
				subdomain={notFoundSubdomain}
				primaryColor={config.primaryColor}
			/>
		);
	}

	// Show maintenance screen if store is inactive
	if (isInactive) {
		return (
			<MaintenanceScreen
				storeName={companyName}
				primaryColor={inactivePrimaryColor}
			/>
		);
	}

	// Show "Coming Soon" screen if KYC not approved — admin can bypass via login
	// Only gate after fresh API response to avoid stale persisted cache blocking approved stores
	if (kycResolved && kycStatus !== 'approved' && !isAdminUser) {
		return (
			<ComingSoonScreen
				storeName={companyName}
				primaryColor={config.primaryColor}
				onAdminLogin={handleComingSoonLogin}
				onPollConfig={reloadConfig}
			/>
		);
	}

	return (
		<GestureHandlerRootView style={{ flex: 1 }}>
			<DynamicMetaTags />
			<View style={{ width: '100%', height: '100%', position: 'relative' }}>
				<NavigationContainerWrapper theme={theme} darkMode={darkMode} />
				<ToastContainer
					position='top-right'
					autoClose={5000}
					hideProgressBar={false}
					newestOnTop={false}
					closeOnClick={false}
					rtl={false}
					pauseOnFocusLoss
					draggable
					pauseOnHover
					theme={darkMode ? 'dark' : 'light'}
					transition={Bounce}
				/>
				
				{/* Overlays - render last so they're on top */}
				{showOverlay && <ThemeTransitionOverlay onComplete={handleOverlayComplete} />}
				<ConfigLoadingOverlay logo={config.appLogo} />
			</View>
		</GestureHandlerRootView>
	);
};

const App = () => {
	return (
		<HelmetProvider>
			<StoreConfigProvider>
				<AppContent />
			</StoreConfigProvider>
		</HelmetProvider>
	);
};

registerRootComponent(App);

// Service worker disabled - Cloudflare Pages provides edge caching
// PWA "Add to Home Screen" works via manifest.json alone
// If you need offline support, fix the WorkboxWebpackPlugin build first

const styles = StyleSheet.create({
	container: {
		flex: 1,
		flexDirection: "column",
		justifyContent: "center",
	},
	overlay: {
		position: "absolute",
		width: "100%",
		height: "100%",
		top: 0,
		backgroundColor: "rgba(0, 0, 0, 0.80)",
	},
	video: {
		position: "absolute",
		width: "100%",
		height: "100%",
		objectFit: "cover",
	},
});
